/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import axios from 'axios';
import urlJoin from 'proper-url-join';
import type { SetRequired } from 'type-fest';

import { PaginatedResult, StoreQueryParams } from '@ibm-watson/shared/ts';
import {
  Environment,
  EnvironmentType,
  SkillReference
} from '../../../../ts/assistant';
import { ReactivateSession } from '../../../../ts/session';
import { selectSkillReferencesFromAssistant } from '../../store/assistants/core-selectors';
import { endpoints } from '../../util/config';

export interface PaginatedEnvironments extends PaginatedResult {
  environments: Environment[];
}

const endpoint = (assistantId: string, environmentId?: string) =>
  urlJoin(
    `${endpoints.store}/v2/assistants/${assistantId}/environments`,
    environmentId
  );

const auditParams: StoreQueryParams = {
  include_audit: true,
  verbose: true
};

export const listEnvironments = async (
  assistantId: string,
  reactivateSession?: ReactivateSession
): Promise<PaginatedEnvironments> => {
  const response = await axios.get<PaginatedEnvironments>(
    urlJoin(endpoint(assistantId), { query: { reactivateSession } }),
    {
      params: {
        ...auditParams
      }
    }
  );

  const environments = response.data.environments
    .map(formatAssistantToEnvironment)
    .map(formatEnvironmentNameInPlace);

  return { environments };
};

/**
 * Create a New IA Environment for a particular New IA assistant.
 */
export const createEnvironment = async (
  { assistant_id, ...data }: SetRequired<Partial<Environment>, 'assistant_id'>,
  params?: StoreQueryParams
): Promise<Environment> => {
  const response = await axios.post<Environment>(endpoint(assistant_id), data, {
    params: { ...auditParams, ...params }
  });

  return formatEnvironmentNameInPlace(response.data);
};

export const getEnvironment = async (
  assistantId: string,
  environmentId: string,
  params?: StoreQueryParams
): Promise<Environment> => {
  const response = await axios.get<Environment>(
    endpoint(assistantId, environmentId),
    {
      params: {
        ...auditParams,
        ...params
      }
    }
  );

  return formatEnvironmentNameInPlace(response.data);
};

export const deleteEnvironment = async (
  assistantId: string,
  environmentId: string,
  params?: StoreQueryParams
): Promise<{}> => {
  const response = await axios.delete<Environment>(
    endpoint(assistantId, environmentId),
    {
      params: {
        ...auditParams,
        ...params
      }
    }
  );

  return response.data;
};

export const updateEnvironment = async (
  {
    assistant_id,
    environment_id,
    ...data
  }: SetRequired<Partial<Environment>, 'environment_id' | 'assistant_id'>,
  params?: StoreQueryParams
): Promise<Environment> => {
  const response = await axios.post<Environment>(
    endpoint(assistant_id, environment_id),
    data,
    {
      params: { ...auditParams, ...params }
    }
  );
  return formatEnvironmentNameInPlace(response.data);
};

/**
 * Publishes the contents of an assistant (called the parent assistant, usually the draft environment) into the
 * specified target assistant (usually the live environment).
 *
 * It will take a snapshot (if necessary) of all the skills of the parent assistant that support that (i.e. action
 * and dialog skills) and assign `said` snapshots to the target assistant.
 *
 * @param {string} sourceEnvironment The draft environment
 * @param {string} targetEnvironment The target environment
 * @param {string} description The description to associate with the published version.
 * @param {Object} params Optional parameters to add to the payload.
 * @return {Promise<Publish>} The publish results
 */
export const publish = async (
  sourceEnvironment: string,
  targetEnvironment: string,
  description: string,
  params?: StoreQueryParams
): Promise<{ is_publishing: boolean }> => {
  const response = await axios.post<{ is_publishing: boolean }>(
    `${urlJoin(`${endpoints.store}/environments`, sourceEnvironment)}/publish`,
    { description },
    {
      params: {
        ...auditParams,
        ...params,
        target_assistant_id: targetEnvironment
      }
    }
  );
  return response.data;
};

/**
 * Reverts the content an environment using the specified snapshot version
 */
export const switchVersion = async (
  assistantId: string,
  environmentId: string,
  skillReferences: SkillReference,
  params?: StoreQueryParams
): Promise<Environment> => {
  const response = await axios.post<Environment>(
    endpoint(assistantId, environmentId),
    {
      skill_references: skillReferences
    },
    {
      params: {
        ...auditParams,
        params
      }
    }
  );

  return formatEnvironmentNameInPlace(response.data);
};

const formatEnvironmentNameInPlace = (
  environment: Environment
): Environment => {
  let name = environment.name;
  if (environment.environment === EnvironmentType.DRAFT) {
    name = EnvironmentType.DRAFT;
  } else if (environment.environment === EnvironmentType.LIVE) {
    name = EnvironmentType.LIVE;
  }

  return {
    ...environment,
    name
  };
};

/**
 * Format the `Assistant` to be an `Environment`. This is used while we transition
 * to multi-environments and the new Store endpoints.
 * FIXME: Remove this when multi-environment is fully supported.
 */
export const formatAssistantToEnvironment = (assistant: any): Environment => {
  if (!assistant.parent_assistant_id) {
    return assistant;
  }

  const isDraft = assistant.assistant_id === assistant.parent_assistant_id;
  return formatEnvironmentNameInPlace({
    name: assistant.name,
    assistant_id: assistant.parent_assistant_id,
    environment_id: assistant.assistant_id,
    description: assistant.description,
    environment:
      assistant.environment ||
      (isDraft ? EnvironmentType.DRAFT : EnvironmentType.LIVE),
    language: assistant.language,
    session_timeout: assistant.session_timeout,
    skill_references: selectSkillReferencesFromAssistant(assistant),
    integration_references: assistant.integration_references,
    role: assistant.role,
    custom_roles: assistant.custom_roles,
    created: assistant.created,
    updated: assistant.updated
  });
};
